import Client from 'App/resources/Model/Client';
import Contact from 'App/resources/Model/Contact';
import Relation from 'App/resources/Model/EntityRelation';
import openModal from 'App/services/Modal';
import AccountRelations from 'Components/Account/AccountRelations/AccountRelations';
import { globalTracker } from 'Helpers/Tracker';
import React, { useCallback, useEffect, useState } from 'react';
import { CardEntity } from './types';
import { calculateRelationsCount } from 'App/pages/ClientCard/ClientCardHelpers';
import ContactRelationResource, {
	type ContactRelation,
	type ContactWithRelations
} from 'App/resources/ContactRelation';
import { Block, Button, Icon, Table, TableColumn, TableHeader, TableRow, Text, Tooltip } from '@upsales/components';
import T from 'Components/Helpers/translate';
import { IAngularEvent } from 'angular';
import logError from 'Helpers/logError';
import { Feature, Product, isAvailable, isAvailableProduct } from 'App/services/FeatureHelperStandalone';
import { openEditContactRelation } from 'App/components/EditContactRelationModal/openEditContactRelation';
import AppService from 'App/legacy/AppService';
import { goToContactCard } from 'App/helpers/contactCardNavigation';

type ContactRelationWithClient = ContactRelation & {
	client?: { id: number; name: string };
};

const ContactRelations = ({ cardEntity }: { cardEntity: CardEntity }) => {
	const contact = cardEntity.data as Contact;
	const customerId = AppService.getCustomerId();
	const [relations, setRelations] = useState<ContactRelationWithClient[]>([]);
	const [loading, setLoading] = useState(true);

	const fetchRelations = useCallback(async () => {
		try {
			const res = await Tools.Contact.customer(customerId).get(contact.id);
			const contactData = res.data as ContactWithRelations;
			const connectedClients: ContactRelationWithClient[] = contactData.connectedClients || [];

			// Resolve client names for each relation
			const resolved = await Promise.all(
				connectedClients.map(async (relation: ContactRelationWithClient) => {
					try {
						const accountRes = await Tools.Account.customer(customerId).get(relation.relatedToClientId);
						return { ...relation, client: { id: accountRes.data.id, name: accountRes.data.name } };
					} catch {
						return relation;
					}
				})
			);
			setRelations(resolved);
		} catch (err) {
			logError(err, 'Failed to load contact relations');
		} finally {
			setLoading(false);
		}
	}, [contact.id, customerId]);

	useEffect(() => {
		fetchRelations();

		const addedListener = Tools.$rootScope.$on(
			'contactRelation.added',
			(_e: IAngularEvent, res: ContactRelation) => {
				if (res.contactId === contact.id) {
					fetchRelations();
				}
			}
		);
		const deletedListener = Tools.$rootScope.$on(
			'contactRelation.deleted',
			(_e: IAngularEvent, res: ContactRelation) => {
				setRelations(prev => {
					const next = prev.filter(r => r.id !== res.id);
					// The relations tab nav hides at zero relations — leave the tab like the Angular controller did
					if (!next.length && prev.length) {
						goToContactCard(contact.id);
					}
					return next;
				});
			}
		);
		const updatedListener = Tools.$rootScope.$on(
			'contactRelation.updated',
			(_e: IAngularEvent, res: ContactRelation) => {
				setRelations(prev => prev.map(r => (r.id === res.id ? { ...r, description: res.description } : r)));
			}
		);

		return () => {
			addedListener();
			deletedListener();
			updatedListener();
		};
	}, [fetchRelations]);

	const openAccount = (clientId: number) => {
		Tools.$state.go('account.dashboard', { id: clientId });
	};

	const editRelation = (relation?: ContactRelationWithClient) => {
		openEditContactRelation({ contact, relation });
	};

	const removeRelation = (relation: ContactRelationWithClient) => {
		if (!relation.id) return;
		openModal('Alert', {
			title: 'client.removeRelation',
			body: 'client.confirmRemoveRelationBody',
			confirmButtonText: 'client.removeRelation',
			headerIcon: 'warning',
			onClose: (confirmed?: boolean) => {
				if (confirmed) {
					ContactRelationResource.delete(relation.id).catch(err =>
						logError(err, 'Failed to remove contact relation')
					);
				}
			}
		});
	};

	if (loading) {
		return null;
	}

	return (
		<Block>
			<Block space="ptl pbl pll prl">
				<Text bold size="lg">
					{T('default.relations')} {'('}
					{relations.length}
					{')'}
				</Text>
			</Block>
			<Table>
				<TableHeader
					columns={[{ title: T('default.account') }, { title: T('default.description') }, { title: '' }]}
				/>
				{relations
					.sort((a, b) => (a.client?.name || '').localeCompare(b.client?.name || ''))
					.map(relation => (
						<TableRow key={relation.id} onClick={() => relation.client && openAccount(relation.client.id)}>
							<TableColumn>{relation.client?.name || ''}</TableColumn>
							<TableColumn>{relation.description}</TableColumn>
							<TableColumn align="right">
								{contact.userEditable ? (
									<>
										<Tooltip title={T('default.edit')} position="left">
											<Button
												type="link"
												onClick={e => {
													e.stopPropagation();
													editRelation(relation);
												}}
											>
												<Icon name="edit" />
											</Button>
										</Tooltip>
										<Tooltip title={T('default.delete')} position="left">
											<Button
												type="link"
												color="red"
												onClick={e => {
													e.stopPropagation();
													removeRelation(relation);
												}}
											>
												<Icon name="trash" />
											</Button>
										</Tooltip>
									</>
								) : null}
							</TableColumn>
						</TableRow>
					))}
			</Table>
			{relations.length === 0 ? (
				<Block space="ptl pbl" className="Text--center">
					<Text color="grey-10">{T('noResult.default')}</Text>
				</Block>
			) : null}
		</Block>
	);
};

const SharedRelations = ({ cardEntity }: { cardEntity: CardEntity }) => {
	if (cardEntity.type === 'contact') {
		return <ContactRelations cardEntity={cardEntity} />;
	}

	const client = cardEntity.data as Client;

	const editRelation = (relation: Relation) => {
		const params = {
			relation: relation,
			account: client,
			customerId: AppService.getCustomerId()
		};
		openModal('AccountRelationModal', params);
	};

	const addRelation = () => {
		const params = {
			account: client,
			customerId: AppService.getCustomerId()
		};
		openModal('AccountRelationModal', params);
	};

	const removeRelation = function (relation: Relation) {
		return Tools.AccountRelation.customer(AppService.getCustomerId())['delete'](relation);
	};

	const hasCompaniesAndContacts = isAvailable(Feature.COMPANIES_AND_CONTACTS);
	const hasActivitiesAndAppointments = isAvailable(Feature.ACTIVITIES_AND_APPOINTMENTS);
	const hasPipeline = isAvailable(Feature.PIPELINE);
	const hasOrders = isAvailable(Feature.ORDERS);
	const hasRecurringOrder = isAvailable(Feature.RECURRING_ORDER);
	const hasDocuments = isAvailable(Feature.DOCUMENTS);
	const hasCompanyRelations = isAvailable(Feature.COMPANY_RELATIONS);
	const hasMarketingEvents = isAvailable(Feature.MARKETING_EVENTS);
	const hasLeads = isAvailable(Feature.LEADS);
	const hasMarketingAutomation = isAvailableProduct(Product.MA);
	const hasEsign = isAvailable(Feature.ESIGN);
	const hasUdo = isAvailable(Feature.UDO);

	const AccountCtrl = {
		account: client,
		createMenuAvailable: {
			contact: hasCompaniesAndContacts,
			activity: hasActivitiesAndAppointments,
			appointment: hasActivitiesAndAppointments,
			opportunity: hasPipeline,
			order: hasOrders,
			agreement: hasRecurringOrder && AppService.getAccessRights().Agreement,
			document: hasDocuments,
			relation: hasCompanyRelations,
			marketEvent: hasMarketingEvents,
			assign: hasLeads,
			resetScore: hasMarketingAutomation,
			uploadFile: hasDocuments,
			esign: hasEsign,
			udo: hasUdo
		},
		track: globalTracker.track,
		addRelation: addRelation,
		removeRelation: removeRelation,
		editRelation: editRelation,
		relationsTotal: calculateRelationsCount(client, { metadata: { total: 0 } })
	};

	return <AccountRelations scope={{ ...Tools.$rootScope, AccountCtrl: AccountCtrl }} />;
};

export default SharedRelations;
